/* fs-carousel: scroll-snap carousel with arrows + progress bar (no dependencies) */
if (!customElements.get('fs-carousel')) {
  customElements.define(
    'fs-carousel',
    class FsCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        if (!this.track) return;
        this.prev = this.querySelector('[data-prev]');
        this.next = this.querySelector('[data-next]');
        this.bar = this.querySelector('[data-progress]');
        this.onScroll = this.update.bind(this);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
        if (this.next) this.next.addEventListener('click', () => this.step(1));
        this.update();
      }
      disconnectedCallback() {
        window.removeEventListener('resize', this.onScroll);
      }
      itemWidth() {
        const item = this.track.querySelector('[data-item]') || this.track.firstElementChild;
        if (!item) return this.track.clientWidth;
        const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap || 0);
        return item.getBoundingClientRect().width + gap;
      }
      step(dir) {
        const perView = Math.max(1, Math.floor(this.track.clientWidth / this.itemWidth()));
        this.track.scrollBy({ left: dir * this.itemWidth() * perView, behavior: 'smooth' });
      }
      update() {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const x = this.track.scrollLeft;
        if (this.prev) this.prev.disabled = x <= 2;
        if (this.next) this.next.disabled = x >= max - 2;
        if (this.bar) {
          if (max <= 0) {
            this.bar.style.width = '100%';
            this.bar.style.transform = 'none';
          } else {
            const ratio = this.track.clientWidth / this.track.scrollWidth;
            const w = Math.max(10, ratio * 100);
            this.bar.style.width = `${w}%`;
            this.bar.style.transform = `translateX(${(x / max) * (100 / w) * (100 - w)}%)`;
          }
        }
        const hasOverflow = max > 2;
        this.classList.toggle('fs-carousel--static', !hasOverflow);
      }
    }
  );
}

/* fs-tabs: minimal tab switcher. [data-tab="key"] buttons + [data-panel="key"] panels */
if (!customElements.get('fs-tabs')) {
  customElements.define(
    'fs-tabs',
    class FsTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = this.querySelectorAll('[data-tab]');
        this.panels = this.querySelectorAll('[data-panel]');
        this.tabs.forEach((t) => t.addEventListener('click', () => this.activate(t.dataset.tab)));
        const first = this.querySelector('[data-tab].is-active') || this.tabs[0];
        if (first) this.activate(first.dataset.tab);
      }
      activate(key) {
        this.tabs.forEach((t) => {
          const on = t.dataset.tab === key;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on);
        });
        this.panels.forEach((p) => {
          const on = p.dataset.panel === key;
          p.classList.toggle('is-active', on);
          p.hidden = !on;
        });
      }
    }
  );
}

/* fs-slider: autoplay hero slider with dots + arrows */
if (!customElements.get('fs-slider')) {
  customElements.define(
    'fs-slider',
    class FsSlider extends HTMLElement {
      connectedCallback() {
        this.slides = [...this.querySelectorAll('[data-slide]')];
        if (this.slides.length < 2) return;
        this.dots = [...this.querySelectorAll('[data-dot]')];
        this.index = 0;
        this.interval = Number(this.dataset.autoplay || 0) * 1000;
        this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
        this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
        this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i, true)));
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.play());
        let startX = 0;
        this.addEventListener('touchstart', (e) => (startX = e.touches[0].clientX), { passive: true });
        this.addEventListener(
          'touchend',
          (e) => {
            const dx = e.changedTouches[0].clientX - startX;
            if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1), true);
          },
          { passive: true }
        );
        this.go(0);
        this.play();
      }
      disconnectedCallback() {
        this.stop();
      }
      go(i, manual) {
        this.index = (i + this.slides.length) % this.slides.length;
        this.slides.forEach((s, k) => {
          s.classList.toggle('is-active', k === this.index);
          s.setAttribute('aria-hidden', k !== this.index);
        });
        this.dots.forEach((d, k) => d.classList.toggle('is-active', k === this.index));
        if (manual) {
          this.stop();
          this.play();
        }
      }
      play() {
        if (!this.interval || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        this.stop();
        this.timer = setInterval(() => this.go(this.index + 1), this.interval);
      }
      stop() {
        if (this.timer) clearInterval(this.timer);
      }
    }
  );
}
